/**
 * Session Capture - Protocol Import shared types (Sprint 3.4 REDO).
 *
 * The Session Capture tab models the canonical prototype workflow at
 * `https://ai-native-prototype.vercel.app/care-pathway-studio.html`:
 * ingest a PROM-instrument selection from the Scrips PROM Engine and
 * emit a FHIR `PlanDefinition` (TLP1) plus a `QuestionnaireResponse`
 * preview. This module owns the shared shapes consumed by every panel
 * (ImportSourcePanel, InstrumentMultiselect, AdminScheduleSelector,
 * FhirOutputPreview, PlanDefinitionMappingPanel) and the publish
 * action.
 *
 * The instrument catalogue is intentionally a closed enum here; the
 * Plan supports 119 instruments via the live PROM Engine, and the top
 * 9 are seeded for the prototype per Sprint 3.4 REDO supervisor brief.
 *
 * HC1 reproducibility: every shape is plain data; no clocks, no
 *   randomness, no hidden mutation.
 * HC5: zero em / en dashes anywhere.
 */

/** Closed instrument catalogue surfaced in the prototype. */
export const PROM_INSTRUMENTS = [
  {
    code: 'MSQ',
    name: 'Menopause Specific Questionnaire',
    items: 29,
    licensing: 'free',
  },
  {
    code: 'PHQ-9',
    name: 'Patient Health Questionnaire 9',
    items: 9,
    licensing: 'free',
  },
  {
    code: 'SF-12',
    name: 'Short Form Health Survey 12',
    items: 12,
    licensing: 'IP',
  },
  {
    code: 'GSRS',
    name: 'Gastrointestinal Symptom Rating Scale',
    items: 15,
    licensing: 'IP',
  },
  {
    code: 'IWQOL',
    name: 'Impact of Weight on Quality of Life',
    items: 31,
    licensing: 'IP',
  },
  {
    code: 'DQOLY',
    name: 'Diabetes Quality of Life for Youth',
    items: 51,
    licensing: 'free',
  },
  {
    code: 'GAD-7',
    name: 'Generalised Anxiety Disorder 7',
    items: 7,
    licensing: 'free',
  },
  {
    code: 'EQ-5D',
    name: 'EuroQol 5 Dimensions',
    items: 5,
    licensing: 'IP',
  },
  {
    code: 'KOOS',
    name: 'Knee injury and Osteoarthritis Outcome Score',
    items: 42,
    licensing: 'free',
  },
] as const;

export type PromInstrumentCode = (typeof PROM_INSTRUMENTS)[number]['code'];

export interface PromInstrumentDescriptor {
  code: PromInstrumentCode;
  name: string;
  items: number;
  licensing: 'IP' | 'free';
}

/** Closed schedule-step vocabulary. Matches the prototype right rail. */
export const ADMIN_SCHEDULE_STEPS = [
  { id: 'step-1-baseline', label: 'Step 1 baseline' },
  { id: 'step-2-monitoring', label: 'Step 2 monitoring' },
  { id: 'step-3-tolerance', label: 'Step 3 tolerance' },
  { id: 'step-4-outcome', label: 'Step 4 outcome' },
] as const;

export type AdminScheduleStepId = (typeof ADMIN_SCHEDULE_STEPS)[number]['id'];

/**
 * Per-step assignment of selected instruments. The keys are the step
 * ids; each value is the subset of `selectedInstruments` administered
 * at that step. The shape is intentionally a Record so React state
 * updates remain trivially immutable.
 */
export type ScheduleAssignments = Record<
  AdminScheduleStepId,
  PromInstrumentCode[]
>;

/** Default empty assignment map. Pure for HC1 reproducibility. */
export function emptyScheduleAssignments(): ScheduleAssignments {
  return {
    'step-1-baseline': [],
    'step-2-monitoring': [],
    'step-3-tolerance': [],
    'step-4-outcome': [],
  };
}

/**
 * The Plan Definition target panel surfaces the canonical TLP1 mapping
 * summary. The numbers and codes mirror the static prototype card.
 */
export interface PlanDefinitionMappingSummary {
  pathwayId: string;
  protocolStepCount: number;
  medicationRxnormCount: number;
  labLoincCount: number;
  promInstrumentCount: number;
  schedulingRulesCount: number;
  exclusionCriteriaCount: number;
}

export const TLP1_MAPPING_SUMMARY: PlanDefinitionMappingSummary = {
  pathwayId: 'tlp1-glp1-v1',
  protocolStepCount: 4,
  medicationRxnormCount: 32,
  labLoincCount: 18,
  promInstrumentCount: 6,
  schedulingRulesCount: 7,
  exclusionCriteriaCount: 11,
};
